import useGraphql from "@/composables/useGraphql";
import { CustomersCustomerType } from "@/types/customers/CustomersCustomerType";
import { defineStore, acceptHMRUpdate } from "pinia";
import * as authQueries from "@/graphql/queries/customers/auth";

interface State {
  token: string | null;
  customer: CustomersCustomerType | null;
  customerRequest: Promise<void> | null;
  originalUrl: string | null;
  customerExisted: boolean | null;
}

// Inside a cross-site iframe (LP embedded in an article page) a default (Lax) cookie is
// dropped, so it must be SameSite=None + Secure; Partitioned keeps it working when
// third-party cookies are blocked. Keep in sync with app/views/functions/_set_global_variables.html.erb.
const CROSS_SITE_COOKIE_ATTRIBUTES = "; SameSite=None; Secure; Partitioned";

const isFramedOverHttps = () => window.self !== window.top && window.location.protocol === "https:";

export const useAuthStore = defineStore({
  id: "auth",

  state: (): State => ({
    token: null,
    customer: null,
    customerRequest: null,
    originalUrl: null,
    customerExisted: null,
  }),

  getters: {
    isLoggedIn: (state) => !!state.token,
  },

  persist: {
    key: "united-cart-auth",
    storage: window.localStorage,
    paths: ["token", "originalUrl", "customerExisted"],
  },

  actions: {
    // Server-side sign-in (LineController#sign_in_and_redirect) can only hand
    // the SPA a cookie - it has no way to reach localStorage, which is where
    // this store actually keeps the token. Without adopting it here the
    // visitor returns from LINE with a valid session cookie that the frontend
    // never sees, so isLoggedIn stays false and every GraphQL call goes out
    // unauthenticated.
    adoptSessionCookie() {
      const match = document.cookie.match(/(?:^|;\s*)united-cart-shop-token=([^;]*)/);
      if (!match) return;

      let cookieToken: string;
      try {
        cookieToken = decodeURIComponent(match[1]);
      } catch {
        return; // a malformed cookie must never abort the boot sequence
      }

      // JS only ever writes this cookie together with the persisted token
      // (setTokenAndCookie / logout), so when the two differ the cookie is the
      // newer one: the server wrote it during a LINE sign-in and the persisted
      // copy is stale - typically a JWT that expired while the visitor was
      // away, which is exactly when LineController#sign_in_and_redirect fires.
      // Letting the stale token win would 401 and log the visitor straight back
      // out of the session the server just created.
      if (!cookieToken || cookieToken === this.token) return;

      this.token = cookieToken;
      this.customer = null;
    },

    logout() {
      this.token = null;

      const currentDate = new Date();
      const nextMonth = new Date(currentDate);
      nextMonth.setDate(currentDate.getDate() - 1);
      document.cookie = `united-cart-shop-token=; expires=${nextMonth.toUTCString()}; path=/`;
      // A partitioned cookie is a separate cookie, so it has to be expired separately.
      if (isFramedOverHttps())
        document.cookie = `united-cart-shop-token=; expires=${nextMonth.toUTCString()}; path=/${CROSS_SITE_COOKIE_ATTRIBUTES}`;
    },

    setTokenAndCookie(token: string) {
      this.token = token;

      const currentDate = new Date();
      const nextMonth = new Date(currentDate);
      nextMonth.setDate(currentDate.getDate() + 30);
      const crossSiteAttributes = isFramedOverHttps() ? CROSS_SITE_COOKIE_ATTRIBUTES : "";
      document.cookie = `united-cart-shop-token=${token}; expires=${nextMonth.toUTCString()}; path=/${crossSiteAttributes}`;
    },

    async fetchCustomerInfo() {
      // LineLoginButton is mounted twice on some shop pages (side menu + page
      // body) and both instances see customer === null in the same tick.
      // Share the in-flight promise so only one customersSelf goes out.
      if (this.customerRequest) return this.customerRequest;

      // errorToasted: false for the same reason as line_info: the toast is
      // raised inside useGraphql, so LineLoginButton's .catch cannot suppress
      // it. A token that expired since the last visit would otherwise pop an
      // English "Unauthorized" over the Japanese purchase form. useGraphql
      // still logs out and still redirects on /shop/ pages.
      this.customerRequest = useGraphql<CustomersCustomerType>(authQueries.self, {}, { errorToasted: false })
        .then((data) => {
          this.customer = data;
        })
        .finally(() => {
          this.customerRequest = null;
        });

      return this.customerRequest;
    },

    clearOriginalUrl() {
      this.originalUrl = null;
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}
